/**
 * @fileoverview Withdraw Prospect Submission mutation hook.
 * @description Withdraws the submission with its ETag as If-Match and refreshes every sales query
 * once it succeeds. The ETag is the one the dialog loaded and showed, so a prospect that changed
 * since is refused (`412` / `409`) and shown for review rather than acted on blindly (3.5.11.0).
 * The command's Idempotency-Key outlives the dialog (see useIdempotentMutation), so a retry after
 * an unknown outcome replays it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useIdempotentMutation } from '@/features/sales/hooks/useIdempotentMutation'
import { withdrawProspect } from '@/features/sales/services/prospects.service'

import type { ProspectWithdrawal } from '@/features/sales/types/prospect.types'

/** The input of one withdraw command. */
export interface WithdrawProspectInput {
  prospectId: string
  etag: string
}

/**
 * @description Withdraws the caller's own pending submission.
 * @param {object} [options] - What follows a success.
 * @param {(result: ProspectWithdrawal, input: WithdrawProspectInput) => void} [options.onSuccess] -
 * Called once it is withdrawn, even when the dialog has unmounted since.
 * @returns {ReturnType<typeof useIdempotentMutation>} The mutation (mutate with the prospect id and
 * the ETag the dialog loaded).
 */
export function useWithdrawProspect({
  onSuccess,
}: {
  onSuccess?: ((result: ProspectWithdrawal, input: WithdrawProspectInput) => void) | undefined
} = {}) {
  return useIdempotentMutation<WithdrawProspectInput, ProspectWithdrawal>({
    action: 'withdraw-prospect',
    /**
     * @description Scopes the key to the prospect being withdrawn, not the version it is sent against.
     * @param {WithdrawProspectInput} input - The withdrawal's input.
     * @returns {string} The prospect's id.
     */
    scopeOf: ({ prospectId }) => prospectId,
    /**
     * @description Sends the withdrawal under the key it is given.
     * @param {WithdrawProspectInput} input - The prospect and the version to send as If-Match.
     * @param {string} key - The Idempotency-Key.
     * @returns {Promise<ProspectWithdrawal>} The withdrawal.
     */
    send: ({ prospectId, etag }, key) => withdrawProspect(prospectId, etag, null, key),
    onSuccess,
  })
}
